/**
 * Game synchronization utilities for fan-out architecture
 * Uses localStorage for lightweight communication between host and audience
 */

import { doc, setDoc, onSnapshot, addDoc, collection, query, where, orderBy, getDocs, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from './firebase';

// 🎯 HOST FUNCTIONS

/**
 * Update the current stage for audience display
 * @param {string} gameId - The game ID
 * @param {number} stage - Current stage number
 * @param {boolean} revealAnswer - Whether to reveal the answer
 * @param {number} scoreAnnouncementIndex - Index of team being announced (-1 = not announcing)
 * @param {Array} teams - Current teams array for score announcements
 */
export const updateHostStage = (gameId, stage, revealAnswer = false, scoreAnnouncementIndex = -1, teams = [], currentSlideIndex = 0) => {
  const stageData = {
    stage,
    revealAnswer,
    scoreAnnouncementIndex,
    teams,
    currentSlideIndex,
    timestamp: new Date().toISOString()
  };
  localStorage.setItem(`hostStage_${gameId}`, JSON.stringify(stageData));
};

/**
 * Clear host stage data when game ends
 * @param {string} gameId - The game ID
 */
export const clearHostStage = (gameId) => {
  localStorage.removeItem(`hostStage_${gameId}`);
};

// 🎯 AUDIENCE FUNCTIONS

/**
 * Get the current host stage
 * @param {string} gameId - The game ID
 * @returns {Object|null} Stage data or null if not found
 */
export const getHostStage = (gameId) => {
  const stageData = localStorage.getItem(`hostStage_${gameId}`);
  return stageData ? JSON.parse(stageData) : null;
};

/**
 * Check if there's a new stage update
 * @param {string} gameId - The game ID
 * @param {number} currentStage - Current stage to compare against
 * @returns {boolean} True if there's a new update
 */
export const hasStageUpdate = (gameId, currentStage) => {
  const stageData = getHostStage(gameId);
  return stageData && stageData.stage !== currentStage;
};

// 🎯 GAME CACHE FUNCTIONS

/**
 * Cache game data locally for offline use
 * @param {string} gameId - The game ID
 * @param {Object} gameData - The game data to cache
 */
export const cacheGameData = (gameId, gameData) => {
  localStorage.setItem(`game_${gameId}`, JSON.stringify(gameData));
};

/**
 * Get cached game data
 * @param {string} gameId - The game ID
 * @returns {Object|null} Cached game data or null if not found
 */
export const getCachedGameData = (gameId) => {
  const cached = localStorage.getItem(`game_${gameId}`);
  return cached ? JSON.parse(cached) : null;
};

/**
 * Cache game state (teams, scores, etc.)
 * @param {string} gameId - The game ID
 * @param {Object} gameState - The game state to cache
 */
export const cacheGameState = (gameId, gameState) => {
  const storageKey = `gameState_${gameId}`;
  const previousData = localStorage.getItem(storageKey);
  
  // Log what we're overwriting
  if (previousData) {
    const parsed = JSON.parse(previousData);
    console.log('💾 OVERWRITING SAVE:', {
      gameId,
      previous: { teams: parsed.teams?.length || 0, stage: parsed.currentStage || 1 },
      new: { teams: gameState.teams?.length || 0, stage: gameState.currentStage || 1 },
      stackTrace: new Error().stack
    });
  }
  
  localStorage.setItem(storageKey, JSON.stringify(gameState));
};

/**
 * Get cached game state
 * @param {string} gameId - The game ID
 * @returns {Object|null} Cached game state or null if not found
 */
export const getCachedGameState = (gameId) => {
  const cached = localStorage.getItem(`gameState_${gameId}`);
  return cached ? JSON.parse(cached) : null;
};

/**
 * Clear all cached data for a game
 * @param {string} gameId - The game ID
 */
export const clearGameCache = (gameId) => {
  localStorage.removeItem(`game_${gameId}`);
  localStorage.removeItem(`gameState_${gameId}`);
  localStorage.removeItem(`hostStage_${gameId}`);
};

// 🎯 UTILITY FUNCTIONS

/**
 * Check if game is cached locally
 * @param {string} gameId - The game ID
 * @returns {boolean} True if game is cached
 */
export const isGameCached = (gameId) => {
  return localStorage.getItem(`game_${gameId}`) !== null;
};

/**
 * Get cache size for monitoring
 * @returns {Object} Cache statistics
 */
export const getCacheStats = () => {
  const keys = Object.keys(localStorage);
  const gameKeys = keys.filter(key => key.startsWith('game_'));
  const stateKeys = keys.filter(key => key.startsWith('gameState_'));
  const stageKeys = keys.filter(key => key.startsWith('hostStage_'));
  
  return {
    totalGames: gameKeys.length,
    activeGames: stateKeys.length,
    activeStages: stageKeys.length,
    totalSize: JSON.stringify(localStorage).length
  };
};

/**
 * Clean up old cache entries (older than 24 hours)
 */
export const cleanupOldCache = () => {
  const keys = Object.keys(localStorage);
  const now = new Date();
  
  keys.forEach(key => {
    if (key.startsWith('gameState_') || key.startsWith('hostStage_')) {
      try {
        const data = JSON.parse(localStorage.getItem(key));
        if (data.lastSaved || data.timestamp) {
          const timestamp = new Date(data.lastSaved || data.timestamp);
          const hoursDiff = (now - timestamp) / (1000 * 60 * 60);
          
          if (hoursDiff > 24) {
            localStorage.removeItem(key);
          }
        }
      } catch (err) {
        // Invalid JSON, remove it
        localStorage.removeItem(key);
      }
    }
  });
};

/**
 * Set the current host stage in Firestore for cross-device sync
 * @param {string} gameId
 * @param {number} stage
 * @param {boolean} revealAnswer
 * @param {number} scoreAnnouncementIndex - Index of team being announced (-1 = not announcing)
 * @param {Array} teams - Current teams array for score announcements
 */
export const setHostStageInFirestore = async (gameId, stage, revealAnswer = false, scoreAnnouncementIndex = -1, teams = []) => {
  // Log raw input values to identify undefined sources
  console.log('🔍 setHostStageInFirestore RAW INPUT:', {
    gameId: gameId,
    stage: stage,
    revealAnswer: revealAnswer,
    scoreAnnouncementIndex: scoreAnnouncementIndex,
    teams: teams,
    gameIdType: typeof gameId,
    stageType: typeof stage,
    revealAnswerType: typeof revealAnswer,
    scoreAnnouncementIndexType: typeof scoreAnnouncementIndex,
    teamsType: typeof teams,
    teamsIsArray: Array.isArray(teams)
  });
  
  // Validate and provide default values to prevent undefined errors
  const validGameId = gameId || '';
  const validStage = typeof stage === 'number' ? stage : 1;
  const validRevealAnswer = typeof revealAnswer === 'boolean' ? revealAnswer : false;
  const validScoreAnnouncementIndex = typeof scoreAnnouncementIndex === 'number' ? scoreAnnouncementIndex : -1;
  const validTeams = Array.isArray(teams) ? teams : [];
  
  // Additional validation - check for any undefined values in teams array
  const validatedTeams = validTeams.map(team => {
    if (!team || typeof team !== 'object') {
      console.warn('⚠️ Invalid team object found:', team);
      return null;
    }
    
    // Ensure all team properties are defined
    const validatedTeam = {
      id: team.id || '',
      name: team.name || '',
      score: typeof team.score === 'number' ? team.score : 0,
      usedPoints: team.usedPoints || {},
      halftimeScore: typeof team.halftimeScore === 'number' ? team.halftimeScore : 0,
      finalScore: typeof team.finalScore === 'number' ? team.finalScore : 0
    };
    
    // Add optional properties if they exist
    if (team.leagueId) validatedTeam.leagueId = team.leagueId;
    if (team.homeVenue) validatedTeam.homeVenue = team.homeVenue;
    if (team.gpa) validatedTeam.gpa = team.gpa;
    
    return validatedTeam;
  }).filter(team => team !== null); // Remove any null teams
  
  console.log('🔧 setHostStageInFirestore with validated data:', {
    gameId: validGameId,
    stage: validStage,
    revealAnswer: validRevealAnswer,
    scoreAnnouncementIndex: validScoreAnnouncementIndex,
    teamsCount: validatedTeams.length,
    validatedTeams: validatedTeams
  });
  
  // Create the document data object
  const documentData = {
    stage: validStage,
    revealAnswer: validRevealAnswer,
    scoreAnnouncementIndex: validScoreAnnouncementIndex,
    teams: validatedTeams,
    timestamp: new Date().toISOString()
  };
  
  // Final validation - check for any undefined values in the document
  Object.keys(documentData).forEach(key => {
    if (documentData[key] === undefined) {
      console.error('❌ UNDEFINED VALUE FOUND in documentData:', key, documentData[key]);
      throw new Error(`Undefined value found in documentData.${key}`);
    }
  });
  
  console.log('✅ Final document data to write:', documentData);
  
  await setDoc(doc(db, 'games', validGameId, 'sync', 'stage'), documentData);
};

/**
 * Subscribe to host stage changes in Firestore
 * @param {string} gameId
 * @param {function} callback - Called with { stage, revealAnswer, scoreAnnouncementIndex, teams }
 * @returns {function} Unsubscribe function
 */
export const subscribeToHostStageInFirestore = (gameId, callback) => {
  const ref = doc(db, 'games', gameId, 'sync', 'stage');
  return onSnapshot(ref, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      callback({ 
        stage: data.stage, 
        revealAnswer: data.revealAnswer,
        scoreAnnouncementIndex: data.scoreAnnouncementIndex || -1,
        teams: data.teams || []
      });
    }
  });
}; 

// 🎯 RECURRING GAME INSTANCE GENERATION
// This function generates actual game instances for recurring games

export const generateRecurringGameInstances = (recurringGame, weeksAhead = 8) => {
  const instances = [];
  const startDate = new Date(recurringGame.recurringStartDate || recurringGame.scheduledDateTime);
  const [hours, minutes] = recurringGame.recurringTime.split(':').map(Number);
  
  // Get selected days
  const selectedDays = Object.entries(recurringGame.recurringDays)
    .filter(([_, checked]) => checked)
    .map(([day, _]) => day);
  
  if (selectedDays.length === 0) {
    console.warn('No recurring days selected for game:', recurringGame.gameTitle);
    return [];
  }
  
  // Day mapping
  const dayMap = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6
  };
  
  // Generate instances for the next X weeks
  for (let week = 0; week < weeksAhead; week++) {
    for (const dayName of selectedDays) {
      const dayOfWeek = dayMap[dayName];
      
      // Calculate the date for this occurrence
      const instanceDate = new Date(startDate);
      instanceDate.setDate(startDate.getDate() + (week * 7) + (dayOfWeek - startDate.getDay() + 7) % 7);
      instanceDate.setHours(hours, minutes, 0, 0);
      
      // Only create instances in the future
      if (instanceDate > new Date()) {
        const instance = {
          ...recurringGame,
          id: `${recurringGame.id || 'recurring'}_${instanceDate.getTime()}`,
          instanceId: `${recurringGame.id || 'recurring'}_${instanceDate.getTime()}`,
          parentRecurringGameId: recurringGame.id,
          scheduledDateTime: instanceDate,
          gameDate: instanceDate.toISOString().split('T')[0],
          gameTime: recurringGame.recurringTime,
          isRecurringInstance: true,
          status: 'scheduled',
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        // Remove recurring-specific fields from instance
        delete instance.recurringDays;
        delete instance.recurringTime;
        delete instance.isRecurring;
        delete instance.recurringStartDate;
        
        instances.push(instance);
      }
    }
  }
  
  console.log(`Generated ${instances.length} game instances for recurring game: ${recurringGame.gameTitle}`);
  return instances;
};

// 🎯 SAVE RECURRING GAME INSTANCES
export const saveRecurringGameInstances = async (recurringGame, weeksAhead = 8) => {
  try {
    const instances = generateRecurringGameInstances(recurringGame, weeksAhead);
    
    if (instances.length === 0) {
      console.warn('No instances to save for recurring game:', recurringGame.gameTitle);
      return [];
    }
    
    // Save instances to Firestore
    const savedInstances = [];
    for (const instance of instances) {
      try {
        const docRef = await addDoc(collection(db, 'scheduledGames'), instance);
        savedInstances.push({
          ...instance,
          id: docRef.id
        });
        console.log(`Saved game instance: ${instance.gameTitle} on ${instance.gameDate}`);
      } catch (error) {
        console.error(`Failed to save game instance: ${instance.gameTitle}`, error);
      }
    }
    
    console.log(`Successfully saved ${savedInstances.length} game instances`);
    return savedInstances;
  } catch (error) {
    console.error('Error saving recurring game instances:', error);
    throw error;
  }
};

// 🎯 CLEANUP OLD RECURRING INSTANCES
export const cleanupOldRecurringInstances = async (recurringGameId) => {
  try {
    // Find and delete old instances that are in the past
    const oldInstancesQuery = query(
      collection(db, 'scheduledGames'),
      where('parentRecurringGameId', '==', recurringGameId),
      where('scheduledDateTime', '<', new Date())
    );
    
    const oldInstancesSnapshot = await getDocs(oldInstancesQuery);
    const deletePromises = oldInstancesSnapshot.docs.map(doc => 
      deleteDoc(doc.ref)
    );
    
    await Promise.all(deletePromises);
    console.log(`Cleaned up ${oldInstancesSnapshot.docs.length} old instances for recurring game: ${recurringGameId}`);
  } catch (error) {
    console.error('Error cleaning up old recurring instances:', error);
  }
};

// 🎯 UPDATE RECURRING GAME SCHEDULE
export const updateRecurringGameSchedule = async (recurringGameId, newSchedule) => {
  try {
    // Update the parent recurring game
    await updateDoc(doc(db, 'scheduledGames', recurringGameId), {
      ...newSchedule,
      updatedAt: new Date()
    });
    
    // Clean up old instances
    await cleanupOldRecurringInstances(recurringGameId);
    
    // Generate new instances
    const updatedGame = {
      id: recurringGameId,
      ...newSchedule
    };
    
    const newInstances = await saveRecurringGameInstances(updatedGame);
    
    console.log(`Updated recurring game schedule: ${recurringGameId}`);
    return newInstances;
  } catch (error) {
    console.error('Error updating recurring game schedule:', error);
    throw error;
  }
};

// 🎯 GET UPCOMING GAME INSTANCES
export const getUpcomingGameInstances = async (recurringGameId, limit = 10) => {
  try {
    const instancesQuery = query(
      collection(db, 'scheduledGames'),
      where('parentRecurringGameId', '==', recurringGameId),
      where('scheduledDateTime', '>', new Date()),
      orderBy('scheduledDateTime', 'asc'),
      limit(limit)
    );
    
    const instancesSnapshot = await getDocs(instancesQuery);
    return instancesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting upcoming game instances:', error);
    return [];
  }
};

// 🎯 MARK GAME INSTANCE AS COMPLETED
export const markGameInstanceCompleted = async (instanceId, results) => {
  try {
    await updateDoc(doc(db, 'scheduledGames', instanceId), {
      status: 'completed',
      completedAt: new Date(),
      results: results,
      updatedAt: new Date()
    });
    
    console.log(`Marked game instance as completed: ${instanceId}`);
  } catch (error) {
    console.error('Error marking game instance as completed:', error);
    throw error;
  }
}; 